import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
  getUserDrafts,
  getAggregatedRankings,
  createDraft as createDraftApi,
  getDraftById,
  recordPick as recordPickApi,
  undoPick as undoPickApi,
  simulateMockPicks as simulateMockPicksApi,
  skipExpiredPick as skipExpiredPickApi,
  extendTimer as extendTimerApi,
  saveGuestDraftToAccount as saveGuestDraftToAccountApi,
  getDraftGuestSlotIndex,
  getGuestSlotIndex,
  storeDraftGuestSlotIndex,
} from '../services/api';

const DraftContext = createContext(null);

const DRAFT_KEY = 'fa_active_draft_id';
// Cadence for polling GET /api/draft/:id while a Mock Draft AI-picks batch is
// mockPicksBatchStatus: "processing" server-side -- each tick's setDraft(fresh) is what makes
// picks stream in, replacing the old client-side setInterval reveal of a pre-fetched array.
// Exported (not just a module-local const) so tests can target exactly this delay when
// short-circuiting setTimeout, without also touching testing-library's own internal timers.
export const MOCK_PICKS_POLL_INTERVAL_MS = 1500;
// Commissioner Mode live sync: much longer than the mock-picks interval above -- that one covers a
// short-lived AI-batch wait, this one can run for the length of an entire human draft (30-60+
// minutes), with every participant's browser polling the whole time. Paused while the tab is
// backgrounded (see the visibilitychange handling below) to bound the real cost of that.
export const DRAFT_ROOM_POLL_INTERVAL_MS = 4000;
// ~90s ceiling (60 * 1.5s) -- a floor under the server's own MockDraftService.STALE_JOB_SECONDS
// (200s) staleness window, so a silently-dead self-invoke doesn't spin the UI with no feedback
// for minutes. Whatever picks did land are already visible either way, since they streamed in via
// polling rather than arriving as one all-or-nothing payload.
const MOCK_PICKS_POLL_MAX_ATTEMPTS = 60;

// Mirrors DraftControls.jsx's calculateTeamIndex / DraftService.calculateTeamIndex.
function calculateTeamIndex(draftType, teamCount, round, pickNumber) {
  if (draftType?.toLowerCase() === 'snake' && round % 2 === 0) {
    return teamCount - pickNumber;
  }
  return pickNumber - 1;
}

/**
 * Provides draft state and actions to all draft UI components.
 * On mount, auto-resumes the first active draft found server-side (if logged in),
 * then falls back to localStorage, then shows setup.
 *
 * @param {string} [draftId] - If provided, loads this specific draft (used for /draft/:id routes).
 */
export function DraftProvider({ draftId: propDraftId, forceNew, children }) {
  const { isLoggedIn, isLoading: authLoading, user } = useAuth();
  const [draft, setDraft] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [aiPicking, setAiPicking] = useState(false);
  // Invalidation token for the in-flight poll loop -- incremented whenever a new poll chain
  // starts (or the provider unmounts), so a stale setTimeout callback from a superseded/cancelled
  // poll can recognize itself as obsolete and no-op instead of calling setDraft with old data.
  const pollTokenRef = useRef(0);

  useEffect(() => {
    // pollTokenRef is a plain mutable counter (not a DOM node ref) that's meant to keep changing
    // across the component's lifetime -- the rule's "ref may have changed by cleanup time"
    // warning doesn't apply to this pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { pollTokenRef.current++; };
  }, []);

  // Fetch aggregated rankings using the draft's scoring format.
  // Re-runs whenever the draft's scoringFormat becomes known or changes.
  // Falls back to half_ppr for pre-feature drafts where scoringFormat is null.
  useEffect(() => {
    const format = draft?.scoringFormat ?? 'half_ppr';
    getAggregatedRankings(format)
      .then(r => setRankings(r || []))
      .catch(() => setRankings([]));
  }, [draft?.scoringFormat]);

  // Determine which draft to load when auth state is known
  useEffect(() => {
    if (authLoading) return;
    // Invalidate any poll chain left over from a previously-loaded draft -- DraftProvider doesn't
    // unmount on a plain draftId prop change (same route, new :draftId param), so without this an
    // in-flight poll for the old draft would keep calling setDraft with the wrong draft's data.
    pollTokenRef.current++;

    async function init() {
      setLoading(true);
      setError(null);
      try {
        if (propDraftId) {
          // Specific draft requested via URL param (/draft/:id)
          const d = await getDraftById(propDraftId);
          setDraft(d);
          localStorage.setItem(DRAFT_KEY, propDraftId);
        } else if (forceNew) {
          // /draft/new — user explicitly wants a new draft; skip auto-resume
          setDraft(null);
          localStorage.removeItem(DRAFT_KEY);
        } else if (isLoggedIn) {
          // Check server for the user's first active draft
          const userDrafts = await getUserDrafts();
          const active = userDrafts.find(d => d.status !== 'completed');
          if (active) {
            const d = await getDraftById(active.draftId);
            setDraft(d);
            localStorage.setItem(DRAFT_KEY, active.draftId);
          } else {
            // Fall back to localStorage (e.g., draft created while offline)
            await tryLoadFromStorage();
          }
        } else {
          // Anonymous — check localStorage only
          await tryLoadFromStorage();
        }
      } catch {
        setDraft(null);
        localStorage.removeItem(DRAFT_KEY);
      } finally {
        setLoading(false);
      }
    }

    async function tryLoadFromStorage() {
      const savedId = localStorage.getItem(DRAFT_KEY);
      if (!savedId) {
        setDraft(null);
        return;
      }
      try {
        const d = await getDraftById(savedId);
        if (d.status !== 'completed') {
          setDraft(d);
        } else {
          setDraft(null);
          localStorage.removeItem(DRAFT_KEY);
        }
      } catch {
        setDraft(null);
        localStorage.removeItem(DRAFT_KEY);
      }
    }

    init();
  }, [authLoading, isLoggedIn, propDraftId, forceNew]);

  // Derive team slots and available players from current draft state
  const picks = draft?.picks || [];
  const draftTeams = draft?.teams || [];
  const draftedNames = new Set(picks.map(p => p.playerName.toLowerCase()));
  // Pre-draft exclusions (dynasty mode): players already rostered from a prior season, hidden the
  // same case-insensitive way as drafted players so they never surface in the pool or AI candidates.
  const excludedNames = new Set((draft?.excludedPlayers || []).map(n => n.toLowerCase()));
  const availablePlayers = rankings.filter(
    r => !draftedNames.has(r.playerName.toLowerCase())
      && !excludedNames.has(r.playerName.toLowerCase())
  );

  async function createDraftFn(req) {
    setError(null);
    const d = await createDraftApi(req);
    setDraft(d);
    localStorage.setItem(DRAFT_KEY, d.draftId);
    return d;
  }

  // Polls GET /api/draft/:id on a self-rescheduling setTimeout loop while a Mock Draft AI-picks
  // batch runs server-side (see DraftInsightsService/LambdaSelfInvokeService's G1 precedent,
  // mirrored here for Mock Draft) -- each tick's setDraft(fresh) is the entire "reveal" mechanism
  // now, since the server's own DraftDTO already reflects every pick made so far and, once done,
  // the real final status/currentRound/currentPick. Token-guarded so a superseded poll chain
  // (draft switched, provider unmounted, a fresh batch started) recognizes itself as stale and
  // stops touching state instead of racing a newer one.
  function pollMockPicks(draftId, attempt, token) {
    if (token !== pollTokenRef.current) return;
    if (attempt >= MOCK_PICKS_POLL_MAX_ATTEMPTS) {
      setError('Still working on AI picks — this is taking longer than usual. Please check back shortly.');
      setAiPicking(false);
      return;
    }
    setTimeout(() => {
      if (token !== pollTokenRef.current) return;
      getDraftById(draftId)
        .then(fresh => {
          if (token !== pollTokenRef.current) return;
          setDraft(fresh);
          if (fresh.mockPicksBatchStatus === 'processing') {
            pollMockPicks(draftId, attempt + 1, token);
          } else {
            setAiPicking(false);
          }
        })
        .catch(err => {
          if (token !== pollTokenRef.current) return;
          console.error('Failed to poll mock draft picks:', err);
          // Transient network hiccup -- still counts toward the cap, but retries rather than
          // stranding the UI on a single failed request.
          pollMockPicks(draftId, attempt + 1, token);
        });
    }, MOCK_PICKS_POLL_INTERVAL_MS);
  }

  // Commissioner Mode live sync: self-rescheduling setTimeout loop, structurally mirroring
  // pollMockPicks above (same pollTokenRef guard so a stale chain from a superseded draft/unmount
  // recognizes itself and stops), but with no attempt cap -- unlike the bounded AI-picks batch wait,
  // this needs to keep running for the whole length of a live human draft. Paused while the tab is
  // hidden (skips both the fetch and the reschedule); the visibilitychange effect below fires one
  // immediate fetch and restarts the chain when the tab becomes visible again.
  function pollDraftRoom(draftId, token, immediate) {
    if (token !== pollTokenRef.current) return;

    const fetchAndReschedule = () => {
      if (token !== pollTokenRef.current) return;
      // getGuestSlotIndex no-ops to null when there's no stored guest token at all (i.e. this
      // viewer is the commissioner or a logged-in member), so this is safe to call unconditionally
      // every tick -- it's what keeps a guest's cached slot self-healing after a commissioner
      // reorder (see api.js's module comment on GUEST_SLOT_KEY_PREFIX), resolved before setDraft so
      // the render that shows the new draft data also has the corrected slot index in place.
      Promise.all([getDraftById(draftId), getGuestSlotIndex(draftId)])
        .then(([fresh, guestSlotIndex]) => {
          if (token !== pollTokenRef.current) return;
          if (guestSlotIndex !== null) storeDraftGuestSlotIndex(draftId, guestSlotIndex);
          setDraft(fresh);
          if (fresh.status === 'completed') return; // nothing left to sync
          pollDraftRoom(draftId, token, false);
        })
        .catch(err => {
          if (token !== pollTokenRef.current) return;
          console.error('Failed to poll draft room:', err);
          // Transient network hiccup -- keep the live-sync loop alive rather than stranding the UI.
          pollDraftRoom(draftId, token, false);
        });
    };

    if (document.hidden) return; // stay paused; the visibilitychange effect resumes this chain
    if (immediate) {
      fetchAndReschedule();
    } else {
      setTimeout(fetchAndReschedule, DRAFT_ROOM_POLL_INTERVAL_MS);
    }
  }

  // Starts (or restarts, on a status transition) Commissioner Mode's live-sync poll loop once a
  // draft is loaded, LOBBY or ACTIVE, and commissioner-mode. Deliberately does not touch solo Draft
  // Mode or Mock Draft's existing pollMockPicks/runAiPicks flow at all -- gated entirely on
  // draft.commissionerMode, which those never set.
  useEffect(() => {
    if (!draft?.commissionerMode) return;
    if (draft.status !== 'lobby' && draft.status !== 'active') return;

    const token = ++pollTokenRef.current;
    pollDraftRoom(draft.draftId, token, false);

    function handleVisibilityChange() {
      if (!document.hidden && token === pollTokenRef.current) {
        pollDraftRoom(draft.draftId, token, true);
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.draftId, draft?.status, draft?.commissionerMode]);

  // Starts a mock draft's AI-picks background batch and begins polling for it. Shared by
  // recordPickFn (triggered by the owner's own pick) and the auto-start/resume effect below
  // (triggered whenever an opponent slot holds the current pick and no batch is already running —
  // e.g. an opponent holds the very first pick, or the page was reloaded mid-batch).
  async function runAiPicks(draftId) {
    setAiPicking(true);
    setError(null);
    const token = ++pollTokenRef.current;
    try {
      const started = await simulateMockPicksApi(draftId);
      if (token !== pollTokenRef.current) return;
      setDraft(started);
      if (started.mockPicksBatchStatus === 'processing') {
        pollMockPicks(draftId, 0, token);
      } else {
        // Already the owner's turn or the draft is complete -- startMockPicksBatch no-ops
        // server-side, nothing to poll for.
        setAiPicking(false);
      }
    } catch (aiErr) {
      if (token !== pollTokenRef.current) return;
      setError(aiErr.response?.data?.error?.message || 'AI picks failed');
      setAiPicking(false);
    }
  }

  // Auto-start/resume AI picking whenever it isn't the owner's turn in an active mock draft.
  // Covers two cases: (1) an opponent holds the very first pick (AI picks never fired in reaction
  // to anything, since that only happens via recordPickFn's own-pick trigger); (2) the page was
  // reloaded/reopened while a batch was already running server-side -- reattaches via polling
  // instead of leaving the draft silently stalled with no recovery path. Requires an explicit
  // isOwnerSlot entry in draft.teams to determine ownership, so it safely no-ops rather than
  // guessing when that data isn't present.
  useEffect(() => {
    if (!draft || !draft.mockDraft || draft.status === 'completed') return;
    if (aiPicking || pickLoading) return;
    const ownerTeam = (draft.teams || []).find(t => t.isOwnerSlot);
    if (!ownerTeam) return;
    const currentTeamIndex = calculateTeamIndex(draft.draftType, draft.teamCount, draft.currentRound, draft.currentPick);
    if (currentTeamIndex === ownerTeam.slotIndex) return;

    if (draft.mockPicksBatchStatus === 'processing') {
      // A batch is already running -- just reattach via polling. The idempotent start endpoint
      // would no-op anyway, but there's no reason to round-trip for it.
      setAiPicking(true);
      setError(null);
      const token = ++pollTokenRef.current;
      pollMockPicks(draft.draftId, 0, token);
    } else {
      runAiPicks(draft.draftId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.draftId, draft?.currentRound, draft?.currentPick, draft?.mockDraft, draft?.status, draft?.picks?.length, draft?.mockPicksBatchStatus]);

  async function recordPickFn(pick) {
    if (!draft || pickLoading) return;
    setPickLoading(true);
    setError(null);
    try {
      const updated = await recordPickApi(draft.draftId, pick);
      setDraft(updated);
      if (updated.mockDraft && updated.status !== 'completed') {
        await runAiPicks(updated.draftId);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to record pick');
    } finally {
      setPickLoading(false);
    }
  }

  async function undoPickFn() {
    if (!draft || pickLoading) return;
    setPickLoading(true);
    setError(null);
    try {
      const updated = await undoPickApi(draft.draftId);
      setDraft(updated);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to undo pick');
    } finally {
      setPickLoading(false);
    }
  }

  // Commissioner Mode timer admin actions -- commissioner-only server-side (DraftService enforces
  // requireOwner), but exposed unconditionally here the same way undoPickFn is; the UI only ever
  // renders the buttons that call these for the commissioner (see PickTimer's isCommissioner prop).
  async function skipExpiredPickFn() {
    if (!draft || pickLoading) return;
    setPickLoading(true);
    setError(null);
    try {
      const updated = await skipExpiredPickApi(draft.draftId);
      setDraft(updated);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to skip the expired pick');
    } finally {
      setPickLoading(false);
    }
  }

  async function extendTimerFn(additionalSeconds) {
    if (!draft || pickLoading) return;
    setPickLoading(true);
    setError(null);
    try {
      const updated = await extendTimerApi(draft.draftId, additionalSeconds);
      setDraft(updated);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to extend the timer');
    } finally {
      setPickLoading(false);
    }
  }

  function clearDraft() {
    setDraft(null);
    localStorage.removeItem(DRAFT_KEY);
  }

  // Links a guest's completed-draft slot to their now-logged-in account. Refreshes draft state
  // with the returned DTO (memberUserId now set on that slot) so isOwner/mySlotIndex-dependent UI
  // (Create League, Link Opponent) picks the newly-linked slot up immediately, without requiring a
  // manual page reload -- unlike the other action functions above, this doesn't touch pickLoading
  // since it's not part of the pick-taking flow.
  async function saveGuestDraftToAccountFn() {
    if (!draft) return;
    const updated = await saveGuestDraftToAccountApi(draft.draftId);
    setDraft(updated);
    return updated;
  }

  function clearError() {
    setError(null);
  }

  const isOwner = !!(draft && user && draft.ownerId === user.userId);

  // Commissioner Mode: "which slot am I" for a non-owner participant, so DraftControls/
  // AvailablePlayers can answer "is it my turn" for a guest/member the same way isOwner already
  // does for the commissioner. A member's slot is discoverable from the poll response itself
  // (memberUserId); a guest's isn't (DraftTeamDTO never exposes which slot a guest token maps to,
  // by design), so that one falls back to what was stored client-side at join time. Always null for
  // non-commissioner-mode drafts (no slot ever carries a memberUserId there), where isOwner alone
  // remains the sole authority, unchanged.
  const myMemberSlot = user ? (draft?.teams || []).find(t => t.memberUserId === user.userId) : null;
  const mySlotIndex = myMemberSlot
    ? myMemberSlot.slotIndex
    : (draft ? getDraftGuestSlotIndex(draft.draftId) : null);

  return (
    <DraftContext.Provider value={{
      draft,
      picks,
      draftTeams,
      availablePlayers,
      rankings,
      loading,
      error,
      pickLoading,
      aiPicking,
      isOwner,
      mySlotIndex,
      createDraft: createDraftFn,
      recordPick: recordPickFn,
      undoPick: undoPickFn,
      skipExpiredPick: skipExpiredPickFn,
      extendTimer: extendTimerFn,
      saveGuestDraftToAccount: saveGuestDraftToAccountFn,
      clearDraft,
      clearError,
    }}>
      {children}
    </DraftContext.Provider>
  );
}

/** Hook to access draft state. Must be used inside <DraftProvider>. */
export function useDraft() {
  const ctx = useContext(DraftContext);
  if (!ctx) throw new Error('useDraft must be used within DraftProvider');
  return ctx;
}

export default DraftContext;
